import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createClient } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService } from 'proto/gen/ts/shirt/v1/admin_pb';
import { ProductService } from 'proto/gen/ts/shirt/v1/product_pb';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { adminProductRow, TEST_IDS } from '../../utils/testIds';
import { transport } from '../../utils/transport';
const adminClient = createClient(AdminService, transport);
const productClient = createClient(ProductService, transport);
export default function ProductManager() {
    const queryClient = useQueryClient();
    const [error, setError] = useState('');
    const { data, isLoading } = useQuery({
        queryKey: [
            'adminProducts'
        ],
        queryFn: ()=>productClient.listProducts({
                featuredOnly: false,
                limit: 0
            })
    });
    const deleteMutation = useMutation({
        mutationFn: (productId)=>adminClient.deleteProduct({
                productId
            }),
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'adminProducts'
                ]
            });
        },
        onError: ()=>setError('Failed to delete product')
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx("p", {
            className: "text-stone-500 p-8",
            children: "Loading products..."
        });
    }
    const products = data?.products ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex justify-between items-center mb-6",
                children: [
                    /*#__PURE__*/ _jsx("h1", {
                        className: "font-heading text-2xl font-bold text-stone-900",
                        children: "Products"
                    }),
                    /*#__PURE__*/ _jsx(Link, {
                        to: "/admin/products/new",
                        "data-testid": TEST_IDS.adminProductsNewButton,
                        className: "bg-stone-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-stone-700",
                        children: "Add Product"
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("div", {
                className: "bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-4",
                children: error
            }),
            products.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-stone-500",
                children: "No products yet. Add one to get started."
            }) : /*#__PURE__*/ _jsx("div", {
                className: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4",
                children: products.map((product)=>/*#__PURE__*/ _jsxs("div", {
                        "data-testid": adminProductRow(product.slug),
                        className: "bg-white border-2 border-stone-100 overflow-hidden",
                        children: [
                            product.imageUrl ? /*#__PURE__*/ _jsx("img", {
                                src: product.imageUrl,
                                alt: product.name,
                                className: "w-full h-40 object-cover"
                            }) : /*#__PURE__*/ _jsx("div", {
                                className: "w-full h-40 bg-stone-100 flex items-center justify-center",
                                children: /*#__PURE__*/ _jsx("span", {
                                    className: "text-sm text-stone-400",
                                    children: "No image"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "p-4",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex justify-between items-start mb-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "font-semibold text-stone-900 text-sm",
                                                children: product.name
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex gap-1",
                                                children: [
                                                    product.featured && /*#__PURE__*/ _jsx("span", {
                                                        className: "text-xs bg-green-100 text-green-700 px-1.5 py-0.5",
                                                        children: "Featured"
                                                    }),
                                                    !product.inStock && /*#__PURE__*/ _jsx("span", {
                                                        className: "text-xs bg-stone-200 text-stone-600 px-1.5 py-0.5",
                                                        children: "Out"
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "text-xs text-stone-500 mb-2",
                                        children: [
                                            "$",
                                            (Number(product.priceCents) / 100).toFixed(2)
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex gap-3",
                                        children: [
                                            /*#__PURE__*/ _jsx(Link, {
                                                to: `/admin/products/${product.id}`,
                                                className: "text-xs text-stone-700 hover:text-stone-900 font-medium underline",
                                                children: "Edit"
                                            }),
                                            /*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                onClick: ()=>{
                                                    if (window.confirm(`Delete ${product.name}?`)) {
                                                        deleteMutation.mutate(product.id);
                                                    }
                                                },
                                                className: "text-xs text-red-600 hover:text-red-800 font-medium",
                                                children: "Delete"
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }, product.id))
            })
        ]
    });
}
